import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Separator } from "../../../components/ui/separator";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  MapPin,
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  FileText,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import API_CONSTRUCTOR from "../../../models/api-model";
import ConfirmPaymentDialog from "../../../components/admin/ConfirmPaymentDialog";
import { getStatusColor, formatStatus, formatDateTime } from "../../../utils/adminUtils";
import HouseFlippingCalculator from "../../../components/calculator";
import { getPropertyField } from "../../../utils/propertyDataHelper";

/**
 * Admin Query Detail Page
 * Shows full details of a query with admin actions
 */
export const AdminQueryDetail = () => {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmPayment, setShowConfirmPayment] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  
  // Initialize API
  const api = API_CONSTRUCTOR();

  useEffect(() => {
    loadQueryDetails();
  }, [queryId]);

  const loadQueryDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/queries/${queryId}`);
      setQuery(response.data);

      // Load calculation if it exists
      if (response.data.calculation_id) {
        try {
          const calcResponse = await api.get(`/calculations/${response.data.calculation_id}`);
          setCalculation(calcResponse.data);
        } catch (err) {
          console.error("Error loading calculation:", err);
        }
      }
    } catch (err) {
      console.error("Error loading query:", err);
      setError(err.response?.data?.error || "Failed to load query details");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentConfirmed = () => {
    loadQueryDetails();
    setShowConfirmPayment(false);
  };

  if (loading) {
    return <div className="text-center py-12">Loading query details...</div>;
  }

  if (error && !loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/admin/queries")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Queries
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadQueryDetails}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!query) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/admin/queries")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Queries
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Query not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate("/admin/queries")}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Queries
      </Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Query Details</h2>
          <p className="text-muted-foreground">Query ID: {query.id}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadQueryDetails}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Badge className={getStatusColor(query.status)} variant="secondary">
            {formatStatus(query.status)}
          </Badge>
        </div>
      </div>

      {/* Property Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Property Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <span className="text-sm font-medium text-muted-foreground">Address</span>
            <p className="text-lg">{query.property_address || getPropertyField(query, 'address', 'N/A')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <span className="text-sm font-medium text-muted-foreground">City</span>
              <p>{query.property_city || getPropertyField(query, 'city', 'N/A')}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">State</span>
              <p>{query.property_state || getPropertyField(query, 'state', 'N/A')}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">ZIP Code</span>
              <p>{query.property_zip || getPropertyField(query, 'zip_code', 'N/A')}</p>
            </div>
          </div>
          {query.property_id && (
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/property/${query.property_id}`, "_blank")}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Property Page
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Homeowner Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="w-5 h-5 mr-2" />
            Homeowner Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <User className="w-4 h-4 mr-1" />
                Name
              </span>
              <p>{query.homeowner_name}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <Mail className="w-4 h-4 mr-1" />
                Email
              </span>
              <p>{query.homeowner_email}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <Phone className="w-4 h-4 mr-1" />
                Phone
              </span>
              <p>{query.homeowner_phone || "N/A"}</p>
            </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Submitted
                </span>
                <p>{formatDateTime(query.created_at)}</p>
              </div>
          </div>
          {query.homeowner_notes && (
            <>
              <Separator />
              <div>
                <span className="text-sm font-medium text-muted-foreground">Notes</span>
                <p className="mt-1">{query.homeowner_notes}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Inspection Payment Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Inspection Payment
          </CardTitle>
        </CardHeader>
        <CardContent>
          {query.inspection_fee_paid ? (
            <div className="space-y-3">
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Payment Confirmed</span>
              </div>
              {query.inspection_fee_paid_date && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Payment Date</span>
                  <p>{formatDateTime(query.inspection_fee_paid_date)}</p>
                </div>
              )}
              {query.inspection_payment_notes && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Payment Notes</span>
                  <p>{query.inspection_payment_notes}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center text-yellow-600">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Payment Pending</span>
              </div>
              <Button onClick={() => setShowConfirmPayment(true)}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirm Payment
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calculation Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Calculation
            </div>
            {calculation && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCalculator(!showCalculator)}
              >
                {showCalculator ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Show Details
                  </>
                )}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {calculation ? (
            <div className="space-y-3">
              <div className={`flex items-center ${
                calculation.status === 'rejected' ? 'text-red-600' :
                calculation.status === 'approved' ? 'text-green-600' :
                calculation.status === 'shared_with_homeowner' ? 'text-blue-600' :
                'text-gray-600'
              }`}>
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">
                  {calculation.status === 'rejected' ? 'Calculation Rejected - Needs Revision' :
                   calculation.status === 'approved' ? 'Calculation Approved' :
                   calculation.status === 'shared_with_homeowner' ? 'Awaiting Homeowner Response' :
                   'Calculation Created'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Created</span>
                  <p>{formatDateTime(calculation.created_at)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Status</span>
                  <Badge variant={
                    calculation.status === 'rejected' ? 'destructive' :
                    calculation.status === 'approved' ? 'default' :
                    calculation.status === 'shared_with_homeowner' ? 'secondary' :
                    'outline'
                  }>
                    {calculation.status.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>
              {calculation.status === 'rejected' && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    Homeowner requested changes. Edit and re-share the calculation.
                  </AlertDescription>
                </Alert>
              )}
              <Button 
                variant={calculation.status === 'rejected' ? 'default' : 'outline'}
                onClick={() => navigate(`/admin/calculations/${calculation.id}`)}
                className={calculation.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                <FileText className="w-4 h-4 mr-2" />
                {calculation.status === 'rejected' ? 'Revise Calculation' :
                 calculation.status === 'approved' ? 'View Calculation' :
                 'Edit Calculation'}
              </Button>
            </div>
          ) : query.status === 'pending_initial_calculation' ? (
            // NEW WORKFLOW: Initial calculation (before payment)
            <div className="space-y-3">
              <div className="flex items-center text-blue-600">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Awaiting Initial Calculation</span>
              </div>
              <Alert className="border-blue-200 bg-blue-50">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Create a preliminary calculation to show the homeowner potential value. 
                  Payment will be required after they accept.
                </AlertDescription>
              </Alert>
              <Button onClick={() => navigate(`/admin/calculations/new/${query.id}`)}>
                <FileText className="w-4 h-4 mr-2" />
                Create Initial Calculation
              </Button>
            </div>
          ) : query.status === 'pending_initial_approval' ? (
            // Waiting for homeowner to accept initial calculation
            <div className="space-y-3">
              <div className="flex items-center text-yellow-600">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Pending Homeowner Review</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Initial calculation shared with homeowner. Waiting for their decision.
              </p>
            </div>
          ) : query.inspection_fee_paid ? (
            // Payment confirmed, ready for detailed calculation
            <div className="space-y-3">
              <div className="flex items-center text-blue-600">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Awaiting Detailed Calculation</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Payment confirmed. You can now create a detailed calculation for this property.
              </p>
              <Button onClick={() => navigate(`/admin/calculations/new/${query.id}`)}>
                <FileText className="w-4 h-4 mr-2" />
                Create Detailed Calculation
              </Button>
            </div>
          ) : (
            // Payment required (old workflow or after initial approval)
            <div className="space-y-3">
              <div className="flex items-center text-yellow-600">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Payment Required</span>
              </div>
              <p className="text-muted-foreground">
                Inspection payment must be confirmed before creating a detailed calculation.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calculator View */}
      {calculation && showCalculator && calculation.calculator_inputs && (
        <Card>
          <CardHeader>
            <CardTitle>Calculation Details (Read-Only)</CardTitle>
          </CardHeader>
          <CardContent>
            <HouseFlippingCalculator
              buildingSquareFeet={calculation.calculator_inputs?.homeArea || 1500}
              estimatedRentAmount={calculation.calculator_inputs?.currentRental || 1800}
              estimatedValue={calculation.calculator_inputs?.presentValue || 700000}
              loanBalance={calculation.calculator_inputs?.loanAmount || 0}
              lotSquareFeet={calculation.calculator_inputs?.lotSize || 5000}
              pool={calculation.calculator_inputs?.hasPool === 'yes'}
              readOnly={true}
              initialInputs={calculation.calculator_inputs}
            />
          </CardContent>
        </Card>
      )}

      {/* Confirm Payment Dialog */}
      {showConfirmPayment && (
        <ConfirmPaymentDialog
          queryId={query.id}
          onClose={() => setShowConfirmPayment(false)}
          onConfirmed={handlePaymentConfirmed}
        />
      )}
    </div>
  );
};

export default AdminQueryDetail;

